import { translate } from "../translate";
import { getPrimaryFundId } from "./fund-listing-utils";

/*
	Reuse all the same logic, adjusted to work with data from Aloglia
 */

/**
 * Sort fund codes based on prefix. Show FE prefixed options first, then other
 * prefixed options, then everything else.
 *
 * @param {string} code Human readable option label.
 * @returns {Number}
 */
const sortOptionByLabel = (code) => {
	if (code.startsWith("FE:")) {
		return 0;
	} else if (!code.includes(":")) {
		return 1;
	} else {
		return 2;
	}
};

/**
 * Determine default purchase options to feature.
 *
 * @param {array} purchaseOptions
 * @param {string} fundName
 * @returns {array}
 */
export const defaultPurchaseOptions = (purchaseOptions, fundName) => {
	// If the fund name indicates it is US focused, prioritze USD purchase
	// options. The advisor is most likely wanting the USD offering.
	const preferredCurrency = /( |^)(US|USD|U\.S\.)( |$)/.test(fundName)
		? "USD"
		: "CAD";

	return [
		purchaseOptions
			.filter((option) => option.purchaseOption === "FE")
			.filter((option) => option.baseCurrency === preferredCurrency)
			.filter((option) => option.series === "Advisor")
			.filter((option) => option.hedged !== true)
			.shift() ||
			purchaseOptions
				.filter((option) => option.series === "FE")
				.filter((option) => option.baseCurrency === preferredCurrency)
				.filter((option) => option.hedged !== true)
				.shift() ||
			purchaseOptions
				.filter((option) => [option.series, option.purchaseOption].includes("FE"))
				.shift(),
		purchaseOptions
			.filter((option) => option.purchaseOption === "F")
			.filter((option) => option.baseCurrency === preferredCurrency)
			.filter((option) => option.series === "Advisor")
			.filter((option) => option.hedged !== true)
			.shift() ||
			purchaseOptions
				.filter((option) => option.series === "F")
				.filter((option) => option.baseCurrency === preferredCurrency)
				.filter((option) => option.hedged !== true)
				.shift() ||
			purchaseOptions
				.filter((option) => [option.series, option.purchaseOption].includes("F"))
				.shift(),
	].filter((option) => option !== undefined);
};

/**
 * @param {object} purchaseOption
 * @returns {string}
 */
export const longPurchaseOptionLabel = (purchaseOption) => {
	const append = () => {
		const response = [
			purchaseOption.hedged === true ? translate("Hedged") : null,
			purchaseOption.baseCurrency === "USD" ? translate("US$") : null,
		].filter((v) => !!v);

		if (response.length <= 0) {
			return "";
		}

		return ` (${response.join(" ")})`;
	};

	if (purchaseOption.purchaseOption && purchaseOption.series) {
		return (
			translate(purchaseOption.series) +
			" " +
			purchaseOption.purchaseOption +
			append() +
			": " +
			purchaseOption.fundservCode
		);
	}

	return purchaseOption.purchaseOption || purchaseOption.series
		? (purchaseOption.purchaseOption || translate(purchaseOption.series)) +
				append() +
				": " +
				purchaseOption.fundservCode
		: purchaseOption.fundservCode;
};

/**
 * @param {object} purchaseOption
 * @returns {string}
 */
export const friendlyPurchaseOptionLabel = (purchaseOption) =>
	purchaseOption.purchaseOption || purchaseOption.series
		? (purchaseOption.purchaseOption || translate(purchaseOption.series)) +
		  ": " +
		  purchaseOption.fundservCode
		: purchaseOption.fundservCode;


/**
 * @param {object} result
 * @returns {object}
 */
export const generateFundCodes = (result) => {
	const purchaseOptions = result.purchaseOptions;
	return {
		...result,
		primaryFundId: getPrimaryFundId(result),
		defaultPurchaseOptions: defaultPurchaseOptions(
			purchaseOptions,
			result.name || ""
		),
		purchaseOptions: purchaseOptions,
		fundCodes: (() =>
			[
				...(purchaseOptions.length &&
					purchaseOptions
						.filter((option) => option && option.fundservCode)
						.map(friendlyPurchaseOptionLabel)),
			].sort(
				(code1, code2) => sortOptionByLabel(code1) - sortOptionByLabel(code2)
			))(),
	};
}
